import { useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { BiHide, BiShow } from "react-icons/bi";
import {
  MdOutlineRotate90DegreesCcw,
  MdOutlineRotate90DegreesCw,
  MdOutlineZoomIn,
  MdOutlineZoomOut,
} from "react-icons/md";
import ReactCrop, { Crop, PixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { canvasPreview } from "./canvasPreview";
import "./styles.css";
import { useDebounceEffect } from "./useDebounceEffects";

interface IProps {
  showModal: boolean;
  onResultCrop: (data: any) => void;
  image: any;
}

interface IToolsProps {
  size: number;
  color: string;
}

const DEFAULT_TOOLS_PROPS: IToolsProps = {
  color: "black",
  size: 25,
};

const SelectAvatarModal: React.FC<IProps> = ({
  showModal,
  onResultCrop,
  image,
}) => {
  const [toolRotateLeft, setToolRotateLeft] = useState(DEFAULT_TOOLS_PROPS);
  const [toolZoomOut, setToolZoomOut] = useState(DEFAULT_TOOLS_PROPS);
  const [toolZoomIn, setToolZoomIn] = useState(DEFAULT_TOOLS_PROPS);
  const [toolRotateRight, setToolRotateRight] = useState(DEFAULT_TOOLS_PROPS);
  const [toolShowPreview, setToolShowPreview] = useState(DEFAULT_TOOLS_PROPS);

  const imgRef = useRef<HTMLImageElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);

  const [showPreview, setShowPreview] = useState(false);

  const onZoom = (val: number) => {
    setScale(scale + val);
  };

  const onRotate = (val: number) => {
    const calc = Math.min(180, Math.max(-180, val));
    setRotate(rotate + calc);
  };

  const getCroppedImage = (
    image: HTMLImageElement,
    crop: PixelCrop,
    scale: number,
    rotate: number
  ): Promise<string> => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("Canvas not supported");
    }

    // Ajusta o tamanho do canvas para o tamanho do crop
    canvas.width = crop.width;
    canvas.height = crop.height;

    // Aplica transformações de escala e rotação
    ctx.save();
    ctx.translate(-crop.x, -crop.y);
    ctx.scale(scale, scale);
    ctx.rotate((rotate * Math.PI) / 180);
    ctx.drawImage(image, 0, 0);
    ctx.restore();

    // Retorna a imagem recortada como base64
    return new Promise((resolve) => {
      previewCanvasRef.current!.toBlob((blob) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        if (blob) reader.readAsDataURL(blob);
      }, "image/jpeg");
    });
  };

  const onResult = async (data?: PixelCrop) => {
    if (data && imgRef.current && completedCrop) {
      const croppedImage = await getCroppedImage(
        imgRef.current,
        completedCrop,
        scale,
        rotate
      );
      onResultCrop(croppedImage);
    } else {
      onResultCrop(false);
    }

    setCrop(undefined);
    setCompletedCrop(undefined);
    setScale(1);
    setRotate(0);
  };

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        canvasPreview(
          imgRef.current,
          previewCanvasRef.current,
          completedCrop,
          scale,
          rotate
        );
      }
    },
    100,
    [completedCrop, scale, rotate]
  );

  return (
    <Modal
      show={showModal}
      onHide={() => onResult(undefined)}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          Alteração da foto de perfil
        </Modal.Title>
      </Modal.Header>
      <Modal.Body
        className="crop-body"
        style={{
          margin: 0,
          padding: 0,
          flexDirection: "column",
          justifyItems: "center",
        }}
      >
        <div className="crop-tools-container">
          <MdOutlineRotate90DegreesCcw
            size={toolRotateLeft.size}
            color={toolRotateLeft.color}
            className="crop-tools-icon"
            onMouseOverCapture={() => {
              setToolRotateLeft({ size: 27, color: "red" });
            }}
            onMouseOutCapture={() => {
              setToolRotateLeft(DEFAULT_TOOLS_PROPS);
            }}
            onClick={() => onRotate(-1)}
          />
          <MdOutlineZoomOut
            size={toolZoomOut.size + 2}
            color={toolZoomOut.color}
            className="crop-tools-icon"
            onMouseOverCapture={() => {
              setToolZoomOut({ size: 27, color: "red" });
            }}
            onMouseOutCapture={() => {
              setToolZoomOut(DEFAULT_TOOLS_PROPS);
            }}
            onClick={() => onZoom(-0.1)}
          />
          <MdOutlineZoomIn
            size={toolZoomIn.size + 2}
            color={toolZoomIn.color}
            className="crop-tools-icon"
            onMouseOverCapture={() => {
              setToolZoomIn({ size: 27, color: "red" });
            }}
            onMouseOutCapture={() => {
              setToolZoomIn(DEFAULT_TOOLS_PROPS);
            }}
            onClick={() => onZoom(+0.1)}
          />
          <MdOutlineRotate90DegreesCw
            size={toolRotateRight.size}
            color={toolRotateRight.color}
            className="crop-tools-icon"
            onMouseOverCapture={() => {
              setToolRotateRight({ size: 27, color: "red" });
            }}
            onMouseOutCapture={() => {
              setToolRotateRight(DEFAULT_TOOLS_PROPS);
            }}
            onClick={() => onRotate(1)}
          />

          {!showPreview ? (
            <BiShow
              title="Mostrar pré-visualização"
              size={toolShowPreview.size}
              color={toolShowPreview.color}
              className="crop-tools-icon"
              onMouseOverCapture={() => {
                setToolShowPreview({ size: 27, color: "red" });
              }}
              onMouseOutCapture={() => {
                setToolShowPreview(DEFAULT_TOOLS_PROPS);
              }}
              onClick={() => setShowPreview(!showPreview)}
            />
          ) : (
            <BiHide
              title="Esconder pré-visualização"
              size={toolShowPreview.size}
              color={toolShowPreview.color}
              className="crop-tools-icon"
              onMouseOverCapture={() => {
                setToolShowPreview({ size: 27, color: "red" });
              }}
              onMouseOutCapture={() => {
                setToolShowPreview(DEFAULT_TOOLS_PROPS);
              }}
              onClick={() => setShowPreview(!showPreview)}
            />
          )}
        </div>

        <ReactCrop
          style={{
            maxWidth: "500px",
            maxHeight: "500px",
            border: "2px dashed red",
            padding: "1px",
          }}
          crop={crop}
          onChange={(c) => setCrop(c)}
          onComplete={(c) => setCompletedCrop(c)}
        >
          <img
            ref={imgRef}
            alt="Crop me"
            src={image}
            style={{
              transform: `scale(${scale}) rotate(${rotate}deg)`,
            }}
          />
        </ReactCrop>

        {!!completedCrop && (
          <div hidden={!showPreview}>
            <canvas
              ref={previewCanvasRef}
              id="canvasPreview"
              style={{
                border: "1px solid black",
                objectFit: "contain",
                width: completedCrop.width,
                height: completedCrop.height,
              }}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div
          style={{ width: "100%" }}
          className="d-flex justify-content-between"
        >
          <Button
            variant="danger"
            type="submit"
            onClick={() => onResult(undefined)}
          >
            Cancelar
          </Button>
          <Button
            variant="success"
            type="submit"
            onClick={() => onResult(completedCrop)}
          >
            Confirmar
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default SelectAvatarModal;
